import querystring from 'query-string'
import { get, pick } from 'lodash'
import { getHistoryFromStorage, setHistoryInStorage } from 'utils/storage'
import { getLabel } from 'utils/parameters'
import { createAction } from 'redux-actions'
import featuresPlugin from '@blazar-plugins/features'
import apiClient from '@blazar-plugins/api-client'

import * as actionNames from './actionNames'

const SEARCH_HISTORY_LENGTH = 4

const requestCompletions = createAction(actionNames.REQUEST_COMPLETIONS)
const receiveCompletions = createAction(actionNames.RECEIVE_COMPLETIONS)
const clearCompletions = createAction(actionNames.CLEAR_COMPLETIONS)
const chooseSuggestion = createAction(actionNames.CHOOSE_SUGGESTION)
const cancelSearch = createAction(actionNames.CANCEL_SEARCH)
const receiveSearchHistory = createAction(actionNames.RECEIVE_SEARCH_HISTORY)
const updateSearchHistory = createAction(actionNames.UPDATE_SEARCH_HISTORY)

const fetchCompletions = (userQuery) => async (dispatch, getState) => {
  const query = pick(userQuery, ['q', 'cg'])
  if (!query.q) {
    // Since the user might have stale completions data in the store we need to remove those.
    dispatch(clearCompletions())
    return Promise.resolve()
  }

  dispatch(requestCompletions(query))
  const autocompleteGoV3IsEnabled = featuresPlugin.selectors.isEnabled(
    getState(),
    'autocomplete-go-v3',
  )

  const version = autocompleteGoV3IsEnabled ? 'v3' : 'v2'

  const url = `autocomplete/${version}/autocomplete?${querystring.stringify(query)}`

  try {
    const response = await dispatch(apiClient.actionCreators.fetch(url))
    const rawCompletions = response.completions
    const completions = Array.isArray(rawCompletions)
      ? rawCompletions.filter((suggestion) => typeof suggestion.q === 'string')
      : []
    return dispatch(receiveCompletions(completions, query))
  } catch {
    return dispatch(receiveCompletions([]))
  }
}

const cgSelector = (state) => querystring.parse(get(state, ['router', 'location', 'search'], '')).cg

const labelSelector = (state, cg) => {
  const parameterData = get(state, ['searchParams', 'byQueryKey', 'cg'], null)
  if (parameterData && cg) {
    return getLabel(parameterData, { cg })
  }
  return undefined // returning undefined so that its not null, false or ''
}

const removeIfExists = (q, cg, historyArray) => {
  const elementIndex = historyArray.findIndex((obj) => {
    // differentiate on q and cg
    return obj.q === q && obj.cg === cg
  })
  if (elementIndex >= 0) {
    historyArray.splice(elementIndex, 1)
  }
  return historyArray
}

const removeFromHistory = (q, cg) => async (dispatch) => {
  const storedHistory = getHistoryFromStorage()
  const newHistory = removeIfExists(q, cg, storedHistory)
  dispatch(updateSearchHistory(newHistory))
  setHistoryInStorage(newHistory)
}

const getHistory = () => async (dispatch) => {
  const history = getHistoryFromStorage()
  // entries should have at least q and history
  const validatedHistory = history.filter((obj) => {
    return !!obj.q && !!obj.history
  })
  dispatch(receiveSearchHistory(validatedHistory))
}

const updateHistory = (newEntry, associatedData = {}) => async (dispatch, getState) => {
  const trimmedEntry = (typeof newEntry === 'string' && newEntry.trim()) || ''
  if (trimmedEntry) {
    // use previous history data if supplied
    let { cg, cgLabel } = associatedData
    const { history } = associatedData
    // if it's a history suggestion without category, skip getting the category from state
    if (!history) {
      /*
      OBS -> We will get the category and category label that is in the state before the search.
      If we add a category dropdown that doesn't update the route immediately,
      we need toget cg and cgLabel from that component's state
      */
      const state = getState()

      if (!cg) {
        cg = cgSelector(state)
      }

      cgLabel = labelSelector(state, cg)
    }
    const storedHistory = getHistoryFromStorage()
    // remove any duplicates first
    const uniqueHistory = removeIfExists(trimmedEntry, cg, storedHistory)
    // make an object
    const newHistoryObject = {
      q: trimmedEntry,
      history: true,
      cg,
      cgLabel,
    }
    // append to array
    uniqueHistory.unshift(newHistoryObject)
    // maintain length of SEARCH_HISTORY_LENGTH
    const maxLengthHistory = uniqueHistory.slice(0, SEARCH_HISTORY_LENGTH)
    // put new array in state and localStorage
    dispatch(updateSearchHistory(maxLengthHistory))
    setHistoryInStorage(maxLengthHistory)
  }
}

export const actionCreators = {
  fetchCompletions,
  cancelSearch,
  chooseSuggestion,
  clearCompletions,
  getHistory,
  updateHistory,
  removeFromHistory,
}
